import {ReportsTableReport} from '../components/ReportsTable';
import {Account, AccountType} from '../components/Search/SearchNav/types';
import {
  useEntityReportsQuery,
  useOrganizationReportsQuery,
} from '../generated/graphql';
import {Override} from '../graphql/teamQuery';
import {Graph} from '../types/graphql';
import {propagateErrorsContext} from './errors';
import {useRampFlagAccountSelector} from './rampFeatureFlags';

type AccountReports = {
  accountReports: Graph<ReportsTableReport>;
  isAccountReportsLoading: boolean;
};

export function useReportsByAccount(
  selectedAccount: Account | null,
  userID: string | null
): AccountReports {
  const enableAccountSelector = useRampFlagAccountSelector();
  const isPersonalEntity =
    selectedAccount?.accountType === AccountType.Personal;
  const {
    data: organizationReportsQuery,
    loading: isOrganizationReportsLoading,
  } = useOrganizationReportsQuery({
    variables: {orgID: selectedAccount?.id ?? '', userID},
    context: propagateErrorsContext(),
    skip: !enableAccountSelector || isPersonalEntity || selectedAccount == null,
  });
  const {data: entityReportsQuery, loading: isEntityReportsLoading} =
    useEntityReportsQuery({
      fetchPolicy: 'network-only',
      context: propagateErrorsContext(),
      variables: {
        entityName: selectedAccount?.name ?? '',
      },
      skip:
        !enableAccountSelector || !isPersonalEntity || selectedAccount == null,
    });
  if (isPersonalEntity) {
    const entityReports = entityReportsQuery?.entity?.views ?? {edges: []};

    return {
      accountReports: entityReports as Override<
        typeof entityReports,
        Graph<ReportsTableReport>
      >,
      isAccountReportsLoading: isEntityReportsLoading,
    };
  } else {
    const organizationReports = organizationReportsQuery?.organization
      ?.views ?? {edges: []};

    return {
      accountReports: organizationReports as Override<
        typeof organizationReports,
        Graph<ReportsTableReport>
      >,
      isAccountReportsLoading: isOrganizationReportsLoading,
    };
  }
}
